import {
	isTripWorkspace,
	tripFilmTimeline,
	type TripWorkspace,
} from "@japangolmok/core";
import { editions, origins } from "@japangolmok/content";
import {
	confirmTripImport,
	MAX_TRIP_FILE_BYTES,
	previewTripImport,
} from "./transfer";
import { customerSectionLabels } from "../content/customer-language";
import {
	deleteRecord,
	getAllRecords,
	getRecord,
	putRecord,
	TRIP_STORE,
} from "./db";

export async function saveTrip(trip: TripWorkspace) {
	if (
		!isTripWorkspace(trip) ||
		!(trip.destination in editions) ||
		!origins.some((origin) => origin.iata === trip.origin)
	)
		throw new Error("INVALID_TRIP_WORKSPACE");
	await putRecord(TRIP_STORE, trip);
}

export async function getTrip(id: string) {
	const trip = await getRecord<TripWorkspace>(TRIP_STORE, id);
	if (
		trip !== undefined &&
		(!isTripWorkspace(trip) ||
			!(trip.destination in editions) ||
			!origins.some((origin) => origin.iata === trip.origin))
	)
		throw new Error("INVALID_TRIP_WORKSPACE");
	return trip;
}

export async function listTrips() {
	const trips = (await getAllRecords<TripWorkspace>(TRIP_STORE)).filter(
		isTripWorkspace,
	);
	return trips.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export async function removeTrip(id: string) {
	await deleteRecord(TRIP_STORE, id);
}

export function serializeTrip(trip: TripWorkspace) {
	const recoveryTrip = {
		...trip,
		filmMoments: (trip.filmMoments ?? []).map((moment) => ({
			...moment,
			photoDataUrl: null,
			photoMimeType: null,
		})),
	};
	return `${JSON.stringify({ exportType: "japangolmok-trip", exportedAt: new Date().toISOString(), trip: recoveryTrip }, null, 2)}\n`;
}

export function parseTripImport(
	input: string,
	nowIso = new Date().toISOString(),
): TripWorkspace {
	return confirmTripImport(previewTripImport(input), undefined, nowIso);
}

const escapeHtml = (value: string) =>
	value.replace(
		/[&<>"']/g,
		(character) =>
			({
				"&": "&amp;",
				"<": "&lt;",
				">": "&gt;",
				'"': "&quot;",
				"'": "&#039;",
			})[character]!,
	);

export function serializeTripFilm(trip: TripWorkspace) {
	if (!isTripWorkspace(trip)) throw new Error("INVALID_TRIP_WORKSPACE");
	const moments = tripFilmTimeline(trip);
	const slides = moments
		.map(
			(moment, index) => `<article class="slide">
    <div class="counter">${String(index + 1).padStart(2, "0")} / ${String(moments.length).padStart(2, "0")}</div>
    ${moment.photoDataUrl ? `<img src="${escapeHtml(moment.photoDataUrl)}" alt="${escapeHtml(moment.placeTitle)}에서 사용자가 고른 장면">` : `<div class="missing" role="img" aria-label="사진 없이 남긴 장면">사진 없이 남긴 장면</div>`}
    <div class="caption"><time>${escapeHtml(moment.dayDate)}</time><h2>${escapeHtml(moment.placeTitle)}</h2><p>${escapeHtml(moment.neighborhood)}</p>${moment.note ? `<p>${escapeHtml(moment.note)}</p>` : ""}${moment.featured ? `<strong>이 날의 대표 장면</strong>` : ""}</div>
  </article>`,
		)
		.join("\n");
	const unvisited = [
		...trip.days.flatMap((day) => day.items),
		...(trip.candidates ?? []),
	].filter((item) => !item.completed || item.returnReason);
	const nextPlaces = unvisited.length
		? `<section class="head"><h2>다음에 이어 걸을 곳</h2><ul>${unvisited.map((item) => `<li>${escapeHtml(item.title)}${item.returnReason ? ` · ${escapeHtml(item.returnReason)}` : " · 아직 다녀오지 않은 곳"}</li>`).join("")}</ul></section>`
		: "";
	const html = `<!doctype html><html lang="ko"><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline'"><meta name="viewport" content="width=device-width"><title>${escapeHtml(trip.title)} · 골목 필름</title><style>
  ${trip.bookTheme ? `@media print{@page{size:A4 landscape;margin:15mm}.film{display:block}.slide{break-after:page;min-height:auto}.head{background:${trip.bookTheme === "red" ? "#a93b27" : trip.bookTheme === "forest" ? "#4e6651" : "#263d63"}}}` : ""}
  *{box-sizing:border-box;overflow-wrap:anywhere}body{margin:0;background:#171411;color:#fff9ed;font-family:system-ui,sans-serif}.head{padding:40px clamp(20px,6vw,80px);border-bottom:1px solid #514b43}.head small{color:#f29a83;letter-spacing:.12em}.head h1{margin:.5rem 0;font-size:clamp(2rem,7vw,5rem)}.head p{max-width:700px;color:#d8cfbf}.film{display:grid;grid-auto-flow:column;grid-auto-columns:100vw;overflow-x:auto;scroll-snap-type:x mandatory}.slide{position:relative;min-height:75vh;display:flex;flex-direction:column;align-items:center;justify-content:flex-start;scroll-snap-align:start;padding:clamp(20px,5vw,72px)}img,.missing{width:min(1100px,100%);height:min(62vh,720px);object-fit:cover;border-radius:2px 32px 2px 2px}.missing{display:grid;place-items:center;border:1px solid #6c6257;background:linear-gradient(135deg,#263d63,#171411);color:#d8cfbf}.caption{position:relative;max-width:min(1100px,100%);width:100%;margin-top:16px;padding:20px;background:rgba(23,20,17,.88);border-left:6px solid #c94b32}.caption time{color:#f29a83}.caption h2{margin:.4rem 0;font-size:clamp(1.7rem,5vw,3.5rem)}.caption p{margin:0}.caption strong{display:block;margin-top:.8rem;color:#d9e6d6}.counter{position:absolute;z-index:2;right:clamp(24px,5vw,80px);top:clamp(24px,5vw,60px);font-family:monospace}@media(prefers-reduced-motion:reduce){*{scroll-behavior:auto!important}}
  </style><header class="head"><small>${customerSectionLabels.film.file}</small><h1>${escapeHtml(trip.title)}</h1><p>${escapeHtml(trip.startDate)}–${escapeHtml(trip.endDate)} · 사진은 이 파일 안에만 들어 있으며 외부로 전송되지 않았어요.</p></header><main class="film">${slides || `<section class="slide"><div class="missing">아직 고른 장면이 없어요.</div></section>`}</main>${nextPlaces}</html>`;
	if (new Blob([html]).size > MAX_TRIP_FILE_BYTES)
		throw new Error("TRIP_FILE_TOO_LARGE");
	return html;
}
